import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { financeApi } from '../../api/financeApi';
import { CURRENCY } from '../../config/currency';
import Modal from '../Modal';
import { todayLocalIso } from '../../utils/dateLocal';

interface NonRevenueCostFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

type CostType = 'VOID' | 'COMPLIMENTARY' | 'STAFF_MEAL';

interface TypeOption {
  value: CostType;
  label: string;
  hint: string;
  color: string;
}

const TYPE_OPTIONS: TypeOption[] = [
  { value: 'VOID', label: 'Void', hint: 'Order cancelled after food was prepared', color: '#D99982' },
  { value: 'COMPLIMENTARY', label: 'Comp', hint: 'Free meal for guest, VIP, or promotion', color: 'var(--text-link)' },
  { value: 'STAFF_MEAL', label: 'Staff Meal', hint: 'Daily food provided to employees', color: 'var(--status-ok)' },
];

const NonRevenueCostForm: React.FC<NonRevenueCostFormProps> = ({ open, onClose, onSaved }) => {
  const { theme } = useTheme();

  const [type, setType] = useState<CostType>('VOID');
  const [estimatedValue, setEstimatedValue] = useState<string>('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(todayLocalIso());
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setType('VOID');
    setEstimatedValue('');
    setReason('');
    setDate(todayLocalIso());
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!estimatedValue || Number(estimatedValue) <= 0) {
      newErrors.estimatedValue = 'Enter a valid amount';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      await financeApi.createNonRevenueCost({
        type,
        estimatedValue: Number(estimatedValue),
        reason: reason.trim() || null,
        date,
      });
      toast.success(`${TYPE_OPTIONS.find((t) => t.value === type)?.label || 'Cost'} recorded`);
      handleClose();
      onSaved();
    } catch (err: unknown) {
      console.error('Error creating non-revenue cost:', err);
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.components?.input?.background || '#FFFFFF',
      borderRadius: '10px',
      '& fieldset': { borderColor: theme.components?.input?.border || '#DDD8D2' },
      '&:hover fieldset': { borderColor: theme.primary },
      '&.Mui-focused fieldset': { borderColor: theme.components?.input?.focus || theme.primary },
    },
    '& .MuiInputLabel-root': { color: theme.gray?.[500] || '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.primary },
    '& .MuiInputBase-input': { color: theme.text },
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Record Void / Comp"
      maxWidth="sm"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: { xs: 0, sm: 1 } }}>
        {/* Type chips */}
        <Box>
          <Typography
            variant="caption"
            sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, mb: 1, display: 'block', fontSize: '0.75rem' }}
          >
            Type
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {TYPE_OPTIONS.map((opt) => {
              const isSelected = type === opt.value;
              return (
                <Box
                  key={opt.value}
                  onClick={() => setType(opt.value)}
                  sx={{
                    flex: 1,
                    py: 1.25,
                    px: 1,
                    textAlign: 'center',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.813rem',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s ease',
                    border: isSelected ? `2px solid ${opt.color}` : `1.5px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                    backgroundColor: isSelected ? `${opt.color}18` : 'transparent',
                    color: isSelected ? opt.color : theme.gray?.[500] || '#666',
                    '&:active': { transform: 'scale(0.96)' },
                  }}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Box>
          {/* Hint text for selected type */}
          {type && (
            <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#888', fontSize: '0.7rem', mt: 0.75, display: 'block' }}>
              {TYPE_OPTIONS.find((t) => t.value === type)?.hint}
            </Typography>
          )}
        </Box>

        {/* Estimated Value */}
        <TextField
          label="Estimated Value"
          type="number"
          value={estimatedValue}
          onChange={(e) => setEstimatedValue(e.target.value)}
          error={!!errors.estimatedValue}
          helperText={errors.estimatedValue}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600 }}>
                  {CURRENCY.symbol}
                </Typography>
              </InputAdornment>
            ),
            inputProps: { min: 0, step: 1, style: { fontSize: '1.25rem', fontWeight: 700 } },
          }}
          sx={inputSx}
          fullWidth
        />

        {/* Reason / Notes */}
        <TextField
          label="Reason / Notes"
          multiline
          minRows={2}
          maxRows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., Customer complaint, Manager comp, Staff lunch"
          sx={inputSx}
          fullWidth
        />

        {/* Date */}
        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={inputSx}
          fullWidth
        />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
          <button
            onClick={handleClose}
            disabled={saving}
            style={{
              width: '35%',
              padding: '12px 16px',
              borderRadius: 10,
              border: '1.5px solid #C8C5BD', /* basil-todo */
              backgroundColor: 'var(--bg-surface)',
              color: 'var(--text-primary)',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
              minHeight: 44,
              opacity: saving ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              width: '65%',
              padding: '12px 16px',
              borderRadius: 10,
              border: 'none',
              backgroundColor: theme.button?.primary?.bg || 'var(--action)',
              color: 'var(--text-inverse)',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
              minHeight: 44,
              opacity: saving ? 0.5 : 1,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </Box>
      </Box>
    </Modal>
  );
};

export default NonRevenueCostForm;
